import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import classNames from 'classnames';

import '../../../css/Multiselect.scss';
import { LessonMode } from '../../../Constants';

import questionService from '../../services/QuestionService';
import responseService from '../../services/ResponseService';
import utilsService from '../../services/UtilsService';

import { useViewportSize } from '../../../hooks/useViewportSize';

import FeedbackIcon from '../FeedbackIcon';
import Guideline from '../tools/Guideline';
import HtmlComponent from '../HtmlComponent';
import OptionalImage from '../OptionalImage';
import PrintQuestionNumber from './PrintQuestionNumber';

import { confirmDialog } from '../dialogs';
import useStyleEvents from '../../../hooks/useStyleEvents';

import { register } from '../../../i18n';

const t = register('AriaLabels');

const Multiselect = observer(({ lessonElementId }) => {
  // Initialize state and get question model and lessonState
  const {
    lessonManager,
    multichoiceManager,
    responseManager,
    toolbarManager,
    questionFeedbackManager
  } = useContext(MobXProviderContext);

  const { width: viewportWidth } = useViewportSize();

  const model = lessonManager.getSlideModel(lessonElementId);
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const feedbackState = questionFeedbackManager.getUiState(lessonElementId);

  const { isEliminatorActive } = toolbarManager;

  const readOnly = (lessonElementState && lessonElementState.readOnly)
    || (feedbackState && feedbackState.showAnswers)
    || lessonManager.isReadOnly;

  const { showAnswers, showAnswerFeedback } = feedbackState;
  const alignRadioSymbolTop = model.anserTopJustify;

  const currentResponseAnswer = (lessonElementState) ? lessonElementState.currentResponseAnswer : null;

  if (!multichoiceManager.hasMultichoiceItems(lessonElementId)) {
    const answers = model?.answers || [];
    const shuffledAnswers = model?.isRandomized
      ? utilsService.guaranteedShuffle([...answers])
      : answers;

    multichoiceManager.setMultichoiceItems(lessonElementId, shuffledAnswers);
  }

  const processedAnswers = lessonElementState
    ? multichoiceManager.getMultichoiceItems(lessonElementId) || []
    : [];

  // The currently selected answers are the elements of currentResponseAnswer.answers that have isCorrect==true
  const selectedAnswerMap = (!lessonElementState) ? [] : currentResponseAnswer.answers.reduce((acc, answer) => {
    acc[answer.id] = answer.isCorrect;
    return acc;
  }, {});

  const eliminatedAnswerMap = (!lessonElementState) ? [] : currentResponseAnswer.answers.reduce((acc, answer) => {
    acc[answer.id] = answer.isEliminated;
    return acc;
  }, {});

  let correctAnswerMap = [];
  if (lessonElementState && model.validation && model.validation.answers) {
    correctAnswerMap = model.validation.answers.reduce((acc, answer) => {
      acc[answer.id] = answer.isCorrect;
      return acc;
    }, {});
  }

  const optionNames = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

  const handleAnswerChange = async (id, newState, lessonElementId) => {
    if (readOnly) {
      return;
    }
    const currentlySelected = currentResponseAnswer.answers.filter(({ isCorrect }) => isCorrect).length;
    if (!model.allowSelectAll && !model.unscored && newState && currentlySelected >= model.correctAnswerCount) {
      // Then we are trying to select an answer when we already have the maximum number of answers selected
      await confirmDialog({
        title: `${utilsService.maxSelected()}`,
        text: `${utilsService.deselect()}`,
        showCancelButton: false,
        confirmButtonText: `${utilsService.confirmOk()}`
      });
    } else {
      await responseService.responseChangeHandler({
        action: isEliminatorActive ? 'eliminate' : 'select',
        id,
        checked: newState
      }, lessonElementId);

      if (isEliminatorActive) {
        toolbarManager.addContentToolUsage('Eliminator');
      }
    }
  };

  // Create handlers for state change
  const handleClick = (data) => {
    // Get the value of the clicked checkbox and whether it is checked or not
    const { value, checked } = data.target;
    handleAnswerChange(value, checked, lessonElementId);
  };

  useStyleEvents(lessonElementId);

  return (
    <div className='multi-select-question'>
      {toolbarManager.isGuidelineOpen && <Guideline lessonElementId={lessonElementId} />}
      <fieldset>
        <legend className='question-legend'>
          <div className='test-item-question' id={`test-item-question-${lessonElementId}`}>
            {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
            <HtmlComponent htmlStr={model.questionText} />
          </div>
        </legend>
        <OptionalImage model={model} runtime={true} />
        <div
          className={classNames('test-item-answers', {
            grid: model.layout === 'grid',
            [`col_${model.gridColumnCount}`]: model.layout === 'grid'
          })}
          role='list'
          style={questionService.getGenericMultichoiceStyleObjs({ model, viewportWidth })?.answerContainerStyle}>
          {
            processedAnswers.map(({ id, text }, index) => {
              const isSelected = selectedAnswerMap[id];
              const isCorrectAnswer = (Object.values(correctAnswerMap).length > 0) ? correctAnswerMap[id] : '';
              const isEliminated = eliminatedAnswerMap[id];
              const symbol = optionNames[index];
              let visualClass = '';
              if (showAnswers || showAnswerFeedback) {
                if (isCorrectAnswer) {
                  visualClass = 'correct';
                } else {
                  visualClass = 'incorrect';
                }
              }

              const allowIsCorrectOverride = toolbarManager.isToolbarAnswerFeedbackActive;
              const isCorrectOverride = isSelected && isCorrectAnswer;

              return (
                <div key={`${id}_${lessonElementId}`} className='multiselect-listitem' role='listitem'>
                  <span className='sr-only' id={`position-${id}_${lessonElementId}`}>
                    {t('positionInList', { position: index + 1, total: processedAnswers.length })}
                  </span>
                  <div
                    aria-checked={isSelected}
                    aria-describedby={`position-${id}_${lessonElementId}`}
                    className={classNames('radio-container', { eliminated: isEliminated })}
                    onClick={(event) => {
                      // Do not toggle if clicking on interactive elements like links or buttons
                      const targetTag = event.target.tagName.toLowerCase();
                      if (targetTag !== 'a' && targetTag !== 'button' && targetTag !== 'input' && !readOnly) {
                        handleAnswerChange(id, !isSelected, lessonElementId);
                      }
                    }}
                    onKeyDown={(event) => {
                      // Only handle Space/Enter when the checkbox div itself is focused, not child elements
                      if (event.target === event.currentTarget && (event.key === ' ' || event.key === 'Enter')) {
                        event.preventDefault();
                        if (!readOnly) {
                          handleAnswerChange(id, !isSelected, lessonElementId);
                        }
                      }
                    }}
                    role='checkbox'
                    style={questionService.getGenericMultichoiceStyleObjs({ model, viewportWidth })?.answerDivStyle}
                    tabIndex={readOnly ? -1 : 0}>
                    <div className='radio-text-container'>
                      <input
                        aria-hidden='true'
                        checked={isSelected}
                        disabled={readOnly}
                        id={`${id}_${lessonElementId}`}
                        onChange={handleClick}
                        tabIndex='-1'
                        type='checkbox'
                        value={id} />
                      <label className={classNames('checkbox-symbol', visualClass, {
                        'selected': isSelected,
                        'disabled': readOnly,
                        'align-top': alignRadioSymbolTop
                      })}>
                        {symbol}
                      </label>
                      <label><HtmlComponent htmlStr={text} /></label>
                      {((showAnswers || showAnswerFeedback) || (allowIsCorrectOverride && isCorrectOverride)) && (
                        <div className='correct-answer-symbol'>
                          <FeedbackIcon
                            answerId={id}
                            isCorrectOverride={allowIsCorrectOverride ? isCorrectOverride : undefined}
                            lessonElementId={lessonElementId} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          }
        </div>
      </fieldset>
    </div>
  );
});
export default Multiselect;
